import { get, post, del, put } from "@/lib/api";
import type {
  CompanyInfo,
  StatusResponse,
  DashboardData,
  NoticeCountResponse,
  WorkStatusParams,
  CallParams,
  UrgentParams,
  UrgentResponse,
  NoticesParams,
  NoticesResponse,
  NoticeType,
  ChatRoomListItem,
  ChatMessageListResponse,
  SendChatMessageRequest,
  SendChatMessageResponse,
  EditChatMessageRequest,
  ChatUploadURLRequest,
  ChatUploadURLResponse,
  ScheduleParams,
  ScheduleResponse,
  PostScheduleParams,
  PostScheduleResponse,
  PostSchedulesBatchParams,
  PostSchedulesBatchResponse,
  DeleteScheduleParams,
  DeleteScheduleResponse,
  ChangeScheduleParams,
  ChangeScheduleResponse,
  PointData,
  AchievesParams,
  AchievesData,
  AttendHistoryParams,
  AttendHistoryItem,
  AttendHistoryDetail,
  CustomerHistoryParams,
  CustomerHistoryItem,
  CustomerDetailData,
  CustomerMemoParams,
  CustomerMemoUpdateParams,
  PostCustomerMemoResponse,
  CustomerReviewParams,
  PostCustomerReviewResponse,
  ReportItem,
  ReportParams,
  PostReportResponse,
  DistributionParams,
  DistributionItem,
  CastAllocationData,
  AllocationRefreshResult,
} from "@/types/cast_api_def";

// ■■■■■■■ API通信層 ■■■■■■■

export const getCompanyInfo = async (): Promise<CompanyInfo> => {
  return await get<CompanyInfo>("/api/cast/company-info");
};

export const getDashboard = async (): Promise<DashboardData> => {
  return await get<DashboardData>("/api/cast/dashboard");
};

// 当日ぶんの配分をCTIから取り込む（#157 追記2）
// 呼ぶのは「アプリを開いた最初の1回（自動）」と「最新の情報を取得ボタン」の2通りだけ
export const postAllocationRefresh = async (): Promise<AllocationRefreshResult> => {
  return await post<AllocationRefreshResult>("/api/cast/allocation/refresh", {});
};

export const getNoticeCount = async (): Promise<NoticeCountResponse> => {
  return await get<NoticeCountResponse>("/api/cast/notice/count");
};

export const postWorkStatus = async (params: WorkStatusParams): Promise<StatusResponse> => {
  return await post<StatusResponse>("/api/cast/work-status", params);
};

export const postCall = async (attendId: string, params: CallParams): Promise<StatusResponse> => {
  return await post<StatusResponse>(`/api/cast/call/${attendId}`, params);
};

export const postUrgent = async (params: UrgentParams): Promise<UrgentResponse> => {
  return await post<UrgentResponse>("/api/cast/urgent", params);
};

export const getNotices = async (params?: NoticesParams): Promise<NoticesResponse> => {
  return await get<NoticesResponse>("/api/cast/notices", params);
};

export const putNoticeRead = async (noticeType: NoticeType, noticeId: string): Promise<StatusResponse> => {
  return await put<StatusResponse>(`/api/cast/notice/${noticeType}/${noticeId}`, {});
};

// ■■■■■■■ チャットAPI ■■■■■■■

export const getChatRooms = async (): Promise<ChatRoomListItem[]> => {
  return await get<ChatRoomListItem[]>("/api/cast/chat/rooms");
};

export const getChatMessages = async (roomId: string, params?: { cursor?: string; direction?: "before" | "after"; limit?: number }): Promise<ChatMessageListResponse> => {
  return await get<ChatMessageListResponse>(`/api/cast/chat/rooms/${roomId}/messages`, params);
};

export const postChatMessage = async (roomId: string, params: SendChatMessageRequest): Promise<SendChatMessageResponse> => {
  return await post<SendChatMessageResponse>(`/api/cast/chat/rooms/${roomId}/messages`, params);
};

export const putChatMessage = async (messageId: string, params: EditChatMessageRequest): Promise<StatusResponse> => {
  return await put<StatusResponse>(`/api/cast/chat/messages/${messageId}`, params);
};

export const deleteChatMessage = async (messageId: string): Promise<StatusResponse> => {
  return await del<StatusResponse>(`/api/cast/chat/messages/${messageId}`);
};

export const postChatRoomRead = async (roomId: string): Promise<StatusResponse> => {
  return await post<StatusResponse>(`/api/cast/chat/rooms/${roomId}/read`, {});
};

export const putChatRoomPin = async (roomId: string): Promise<StatusResponse> => {
  return await put<StatusResponse>(`/api/cast/chat/rooms/${roomId}/pin`, {});
};

export const postChatUploadURL = async (params: ChatUploadURLRequest): Promise<ChatUploadURLResponse> => {
  return await post<ChatUploadURLResponse>("/api/cast/chat/upload-url", params);
};

export const getSchedule = async (params?: ScheduleParams): Promise<ScheduleResponse> => {
  return await get<ScheduleResponse>("/api/cast/schedule", params);
};

export const postSchedule = async (params: PostScheduleParams): Promise<PostScheduleResponse> => {
  return await post<PostScheduleResponse>("/api/cast/schedule", params);
};

export const postSchedules = async (params: PostSchedulesBatchParams): Promise<PostSchedulesBatchResponse> => {
  return await post<PostSchedulesBatchResponse>("/api/cast/schedule/batch", params);
};

export const deleteSchedule = async (scheduleId: string, params: DeleteScheduleParams): Promise<DeleteScheduleResponse> => {
  return await del<DeleteScheduleResponse>(`/api/cast/schedule/delete/${scheduleId}`, params);
};

export const changeSchedule = async (scheduleId: string, params: ChangeScheduleParams): Promise<ChangeScheduleResponse> => {
  return await post<ChangeScheduleResponse>(`/api/cast/schedule/change/${scheduleId}`, params);
};

export const getPoint = async (): Promise<PointData> => {
  return await get<PointData>("/api/cast/point");
};

export const getAchieves = async (params?: AchievesParams): Promise<AchievesData> => {
  return await get<AchievesData>("/api/cast/achieves", params);
};

export const getAttendHistory = async (params?: AttendHistoryParams): Promise<AttendHistoryItem[]> => {
  return await get<AttendHistoryItem[]>("/api/cast/attend/history", params);
};

export const getAttendHistoryDetail = async (id: string): Promise<AttendHistoryDetail> => {
  return await get<AttendHistoryDetail>(`/api/cast/attend/history/${id}`);
};

export const getCustomerHistory = async (params?: CustomerHistoryParams): Promise<CustomerHistoryItem[]> => {
  return await get<CustomerHistoryItem[]>("/api/cast/customer/history", params);
};

export const getCustomerDetail = async (id: string): Promise<CustomerDetailData> => {
  return await get<CustomerDetailData>(`/api/cast/customer/detail/${id}`);
};

export const postCustomerMemo = async (params: CustomerMemoParams): Promise<PostCustomerMemoResponse> => {
  return await post<PostCustomerMemoResponse>("/api/cast/customer/memo", params);
};

export const putCustomerMemo = async (id: string, params: CustomerMemoUpdateParams): Promise<StatusResponse> => {
  return await put<StatusResponse>(`/api/cast/customer/memo/${id}`, params);
};

export const deleteCustomerMemo = async (id: string): Promise<StatusResponse> => {
  return await del<StatusResponse>(`/api/cast/customer/memo/${id}`);
};

export const postCustomerReview = async (customerId: string, params: CustomerReviewParams): Promise<PostCustomerReviewResponse> => {
  return await post<PostCustomerReviewResponse>(`/api/cast/customer/review/${customerId}`, params);
};

// #246: レビューもメモと同じく編集/削除できる（id 指定）
export const putCustomerReview = async (id: string, params: CustomerReviewParams): Promise<StatusResponse> => {
  return await put<StatusResponse>(`/api/cast/customer/review/${id}`, params);
};

export const deleteCustomerReview = async (id: string): Promise<StatusResponse> => {
  return await del<StatusResponse>(`/api/cast/customer/review/${id}`);
};

export const getReports = async (): Promise<ReportItem[]> => {
  return await get<ReportItem[]>("/api/cast/reports");
};

export const postReport = async (params: ReportParams): Promise<PostReportResponse> => {
  return await post<PostReportResponse>("/api/cast/report", params);
};

export const getDistribution = async (params?: DistributionParams): Promise<DistributionItem[]> => {
  return await get<DistributionItem[]>("/api/cast/distribution", params);
};

export const getCastAllocation = async (date: string): Promise<CastAllocationData> => {
  return await get<CastAllocationData>("/api/cast/allocation", { date });
};

// ■■■■■■■ API層 ここまで ■■■■■■■